import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Search, ChevronLeft, ChevronRight, Eye, Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge, EmptyState } from '@/components/design-system'
import CommandCenterLayout from '@/frontend/layouts/CommandCenterLayout'
import {
  FleetMachineSummary,
  FLEET_MACHINE_ENVIRONMENT_LABELS,
  FLEET_MACHINE_PROVIDER_LABELS,
  FLEET_MACHINE_CONTROL_MODE_LABELS,
} from '@/frontend/types/fleetMachine'

interface OrganizationRow {
  id: string
  name: string
  slug: string
  workers_count: number
  default_fleet_machine: FleetMachineSummary | null
}

interface Pagination { page: number; totalPages: number; total: number }

interface Props {
  organizations: OrganizationRow[]
  assignable_fleet_machines: FleetMachineSummary[]
  pagination: Pagination
  search: string
}

const UNASSIGNED_VALUE = '__unassigned__'

function currentValue(org: OrganizationRow): string {
  return org.default_fleet_machine?.id ?? UNASSIGNED_VALUE
}

export default function Index({ organizations, assignable_fleet_machines, pagination, search }: Props) {
  const [query, setQuery] = useState(search)
  const [selections, setSelections] = useState<Record<string, string>>({})
  const [savingId, setSavingId] = useState<string | null>(null)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/cc/organizations', { search: query }, { preserveState: true })
  }

  const goToPage = (page: number) =>
    router.get('/cc/organizations', { search, page }, { preserveState: true })

  const selectedFor = (org: OrganizationRow) => selections[org.id] ?? currentValue(org)

  const isDirty = (org: OrganizationRow) => selectedFor(org) !== currentValue(org)

  const handleSave = (org: OrganizationRow) => {
    const selected = selectedFor(org)
    const payload = selected === UNASSIGNED_VALUE ? '' : selected
    setSavingId(org.id)
    router.patch(`/cc/organizations/${org.id}/assign_machine`, { fleet_machine_id: payload }, {
      onFinish: () => setSavingId(null),
      onSuccess: () => setSelections((prev) => {
        const next = { ...prev }
        delete next[org.id]
        return next
      }),
    })
  }

  const machineOptions = [
    { label: 'Sem atribuição', value: UNASSIGNED_VALUE },
    ...assignable_fleet_machines.map((m) => ({
      label: `${m.name} — ${FLEET_MACHINE_ENVIRONMENT_LABELS[m.environment]}`,
      value: m.id,
    })),
  ]

  const hasNoResults = organizations.length === 0 && !!search
  const hasNoOrgsAtAll = organizations.length === 0 && !search

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-foreground">Organizações</h2>
          <span className="text-sm text-muted-foreground">({pagination.total})</span>
        </div>
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nome ou slug..."
              aria-label="Buscar organizações"
              className="pl-9 pr-3 py-2 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring w-72"
            />
          </div>
          <Button type="submit" variant="outline" size="sm">Buscar</Button>
        </form>
      </div>

      {hasNoOrgsAtAll ? (
        <EmptyState
          title="Nenhuma organização cadastrada"
          description="Organizações aparecem aqui conforme são criadas."
        />
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="bg-muted border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Nome</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Slug</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Agentes</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Máquina atual</th>
                  <th className="text-left px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider min-w-[280px]">Atribuir máquina</th>
                  <th className="text-right px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {organizations.map((org) => {
                  const current = org.default_fleet_machine
                  const saving = savingId === org.id
                  return (
                    <tr key={org.id} className="hover:bg-accent transition-colors">
                      <td className="px-4 py-3 text-sm font-medium text-foreground">{org.name}</td>
                      <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{org.slug}</td>
                      <td className="px-4 py-3"><Badge tone={org.workers_count > 0 ? 'info' : undefined}>{org.workers_count}</Badge></td>
                      <td className="px-4 py-3 text-sm">
                        {current ? (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className="font-medium text-foreground">{current.name}</span>
                            <Badge>{FLEET_MACHINE_PROVIDER_LABELS[current.provider]}</Badge>
                            <Badge>{FLEET_MACHINE_ENVIRONMENT_LABELS[current.environment]}</Badge>
                            <Badge>{FLEET_MACHINE_CONTROL_MODE_LABELS[current.control_mode]}</Badge>
                            {!current.enabled && <Badge tone="warning">Desabilitada</Badge>}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={selectedFor(org)}
                          onChange={(e) => setSelections((prev) => ({ ...prev, [org.id]: e.target.value }))}
                          aria-label={`Atribuir máquina para ${org.name}`}
                          className="w-full h-9 px-2 text-sm bg-background border border-border rounded focus:outline-none focus:ring-2 focus:ring-ring"
                        >
                          {machineOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" asChild>
                            <Link href={`/cc/organizations/${org.id}`} aria-label={`Ver detalhes de ${org.name}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleSave(org)}
                            disabled={!isDirty(org) || saving}
                            aria-label={`Salvar atribuição de ${org.name}`}
                          >
                            <Save className="h-4 w-4 mr-1" />
                            {saving ? 'Salvando...' : 'Salvar'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {hasNoResults && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-sm text-muted-foreground">Nenhuma organização encontrada.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted">
              <span className="text-sm text-muted-foreground">Página {pagination.page} de {pagination.totalPages}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => goToPage(pagination.page - 1)} aria-label="Página anterior">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => goToPage(pagination.page + 1)} aria-label="Próxima página">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

Index.layout = (page: React.ReactElement) => <CommandCenterLayout>{page}</CommandCenterLayout>
